import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import { useMobile } from "../../hooks/custom/useMobile";
import useTab from "../../hooks/custom/useTab";

const ApplicationList = () => {
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<any | null>(
    null,
  );

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const mobile = useMobile();
  const tab = useTab();

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_URL}`);
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleSortByDate = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  /* --------- SORTED DATA --------- */
  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [applications, sortOrder]);

  useEffect(() => {
    return () => {
      const el = document.getElementById("applicationOffcanvas");
      if (el) {
        const instance = Offcanvas.getInstance(el);
        instance?.dispose();
      }

      document
        .querySelectorAll(".offcanvas-backdrop")
        .forEach((b) => b.remove());
    };
  }, []);

  const openOffcanvas = (app: any) => {
    setSelectedApplication(app);

    const el = document.getElementById("applicationOffcanvas");
    if (!el) return;

    let instance = Offcanvas.getInstance(el);
    if (!instance) {
      instance = new Offcanvas(el);
    }
    instance.show();
  };

  const SortIcon = ({ order }: { order: "asc" | "desc" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
      width="16"
      height="16"
      style={{
        marginLeft: "6px",
        transition: "transform 0.2s ease",
        transform: order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <path d="M130.4 268.2C135.4 280.2 147 288 160 288L480 288C492.9 288 504.6 280.2 509.6 268.2C514.6 256.2 511.8 242.5 502.7 233.3L342.7 73.3C330.2 60.8 309.9 60.8 297.4 73.3L137.4 233.3C128.2 242.5 125.5 256.2 130.5 268.2zM130.4 371.7C125.4 383.7 128.2 397.4 137.3 406.6L297.3 566.6C309.8 579.1 330.1 579.1 342.6 566.6L502.6 406.6C511.8 397.4 514.5 383.7 509.5 371.7C504.5 359.7 492.9 352 480 352L160 352C147.1 352 135.4 359.8 130.4 371.8z" />
    </svg>
  );

  const DetailRow = ({
    label,
    value,
  }: {
    label: string;
    value?: React.ReactNode;
  }) => {
    const displayValue =
      value === null || value === undefined || value === "" || value === false
        ? "-"
        : value;
    return (
      <tr>
        {" "}
        <td style={{ fontWeight: 600, width: "40%" }}>{label}</td>{" "}
        <td>{displayValue}</td>{" "}
      </tr>
    );
  };

  return (
    <section>
      <div className="td_height_80 td_height_lg_80" />
      <div className="td_height_80 td_height_lg_40" />

      <div className="container">
        <h2 className="text-center mb-4">List of Applications</h2>

        <div style={{ overflowX: "auto" }}>
          <table width="100%" border={1} cellPadding={10}>
            <thead>
              <tr>
                <th>SN</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Gender</th>
                <th>Phone</th>
                <th>Nationality</th>

                <th
                  onClick={handleSortByDate}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Applied At <SortIcon order={sortOrder} />
                </th>

                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} align="center">
                    Loading...
                  </td>
                </tr>
              ) : sortedApplications.length === 0 ? (
                <tr>
                  <td colSpan={8} align="center">
                    No applications found
                  </td>
                </tr>
              ) : (
                sortedApplications.map((app, index) => (
                  <tr key={app.id}>
                    <td>{index + 1}</td>
                    <td>{app.full_name}</td>
                    <td>{app.email}</td>
                    <td>
                      {app.gender
                        ? app.gender.charAt(0).toUpperCase() +
                          app.gender.slice(1)
                        : "-"}
                    </td>
                    <td>{app.mobile_number || "-"}</td>
                    <td>{app.nationality || "-"}</td>
                    <td>{new Date(app.created_at).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openOffcanvas(app)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      >
                        👁
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className={`offcanvas ${
          mobile || tab ? "offcanvas-bottom" : "offcanvas-end"
        }`}
        tabIndex={-1}
        id="applicationOffcanvas"
        data-bs-backdrop="true"
        data-bs-scroll="false"
        style={{ width: mobile || tab ? "100%" : "900px" }}
      >
        <div className="offcanvas-body">
          <button
            type="button"
            className="btn-close mb-3"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />

          <h4 className="mb-3">Application Details</h4>
          {selectedApplication ? (
            <div className="table-responsive">
              <table className="table table-sm">
                <tbody>
                  <DetailRow
                    label="Program"
                    value={selectedApplication.program_option}
                  />
                  <DetailRow
                    label="Full Name"
                    value={selectedApplication.full_name}
                  />
                  <DetailRow
                    label="Date of Birth"
                    value={new Date(
                      selectedApplication.dob,
                    ).toLocaleDateString()}
                  />
                  <DetailRow
                    label="Gender"
                    value={
                      selectedApplication.gender
                        ? selectedApplication.gender.charAt(0).toUpperCase() +
                          selectedApplication.gender.slice(1)
                        : null
                    }
                  />
                  <DetailRow
                    label="Nationality"
                    value={selectedApplication.nationality}
                  />
                  <DetailRow
                    label="Ethnicity"
                    value={selectedApplication.ethnicity}
                  />
                  <DetailRow label="Email" value={selectedApplication.email} />
                  <DetailRow
                    label="Mobile Number"
                    value={selectedApplication.mobile_number}
                  />

                  <DetailRow
                    label="Current Address"
                    value={selectedApplication.current_address}
                  />
                  <DetailRow
                    label="Permanent Address"
                    value={selectedApplication.permanent_address}
                  />

                  <DetailRow
                    label="Guardian Name"
                    value={selectedApplication.guardian_name}
                  />
                  <DetailRow
                    label="Guardian Relation"
                    value={selectedApplication.guardian_relation}
                  />

                  <DetailRow
                    label="Highest Degree"
                    value={selectedApplication.highest_degree}
                  />

                  <DetailRow
                    label="Major Subject"
                    value={selectedApplication.major_subject}
                  />

                  <DetailRow
                    label="Academic Award"
                    value={
                      selectedApplication.academic_awards_file_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.academic_awards_file_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Research Project"
                    value={
                      selectedApplication.research_projects_file_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.research_projects_file_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Authored/Co-Authored Publication"
                    value={
                      selectedApplication.authored_publications_file_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.authored_publications_file_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Conference"
                    value={
                      selectedApplication.conference_attended_file_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.conference_attended_file_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Work Experience"
                    value={
                      selectedApplication.work_experience
                        ? `${selectedApplication.work_exp_company_name} (${selectedApplication.work_experience_years} years)`
                        : "No"
                    }
                  />

                  <DetailRow
                    label="Internship/Voluntary Experience"
                    value={
                      selectedApplication.internship_volunteer_experience
                        ? `${selectedApplication.internship_volunteer_organization} (${selectedApplication.internship_volunteer_duration} months)`
                        : "No"
                    }
                  />

                  <DetailRow
                    label="Finance Method"
                    value={selectedApplication.finance_mpp_education}
                  />

                  <DetailRow
                    label="Referee One"
                    value={`${selectedApplication.referee1_name} | ${selectedApplication.referee1_email} | ${selectedApplication.referee1_phone}`}
                  />

                  <DetailRow
                    label="Referee Two"
                    value={`${selectedApplication.referee2_name} | ${selectedApplication.referee2_email} | ${selectedApplication.referee2_phone}`}
                  />

                  <DetailRow
                    label="Citizenship"
                    value={
                      selectedApplication.supporting_document_citizenship_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.supporting_document_citizenship_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="CV"
                    value={
                      selectedApplication.supporting_document_cv_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.supporting_document_cv_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Certificate"
                    value={
                      selectedApplication.supporting_document_certificate_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.supporting_document_certificate_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Profile Photo"
                    value={
                      selectedApplication.profile_photo_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.profile_photo_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Payment Proof"
                    value={
                      selectedApplication.payment_confirmation_url ? (
                        <a
                          href={`${process.env.REACT_APP_URL}${selectedApplication.payment_confirmation_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View File
                        </a>
                      ) : null
                    }
                  />

                  <DetailRow
                    label="Applied At"
                    value={new Date(
                      selectedApplication.created_at,
                    ).toLocaleString()}
                  />
                </tbody>
              </table>
            </div>
          ) : (
            <p>No application selected</p>
          )}
        </div>
      </div>

      <div className="td_height_30" />
    </section>
  );
};

export default ApplicationList;
